import { TemplateFolderCreateModal } from "@/pages/talent/templates/TemplateFolderCreateModal.tsx";
import {
  useTemplateFolderDelete,
  useTemplateFolders,
} from "@/queries/forms/template-folder.query.ts";
import { TemplateFolderModule } from "@/types/forms/form-template-folder.types.ts";
import {
  Card,
  ConfirmationModal,
  DropdownMenuItem,
  FontAwesomeIcon,
  Stack,
  Tag,
  useModals,
} from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";

export const TemplateFoldersView = ({
  module,
  canManage,
}: {
  module: TemplateFolderModule;
  canManage: boolean;
}) => {
  const { t } = useTranslation();
  const { openModal } = useModals();
  const { data: folders, isLoading } = useTemplateFolders(module);
  const folderDelete = useTemplateFolderDelete(module);
  return (
    <Stack
      direction="horizontal"
      gap="md"
      className="grid md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 px-16"
    >
      {isLoading ? (
        <>
          <Card isLoading />
          <Card isLoading />
          <Card isLoading />
        </>
      ) : (
        <>
          <Card
            header={t("common:all_templates")}
            to={`/templates/${module}/all`}
            buttonsSlot={
              <Tag>
                {t("plurals:templates_count", {
                  count: (folders ?? []).reduce(
                    (total, folder) => total + folder.template_count,
                    0
                  ),
                })}
              </Tag>
            }
          />
          {(folders ?? []).map((folder) => (
            <Card
              key={folder.id}
              header={folder.title}
              to={`/templates/${module}/folders/${folder.id}`}
              buttonsSlot={
                <Tag>{t("plurals:templates_count", { count: folder.template_count })}</Tag>
              }
              menu={
                canManage && (
                  <>
                    <DropdownMenuItem
                      title={t("common:rename_template_folder")}
                      leftIcon={<FontAwesomeIcon icon="fa-light fa-pen-to-square" />}
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.preventDefault();
                        openModal(TemplateFolderCreateModal, { module, folder });
                      }}
                    />
                    {folder.template_count === 0 && (
                      <DropdownMenuItem
                        title={t("common:delete_template_folder")}
                        variant="menuDanger"
                        leftIcon={<FontAwesomeIcon icon="fa-light fa-trash" />}
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                          e.preventDefault();
                          openModal(ConfirmationModal, {
                            question: t("common:delete_template_folder_named", {
                              title: folder.title,
                            }),
                            text: t("common:delete_confirm"),
                            acceptTitle: t("common:delete"),
                            onAccept: async () => {
                              await folderDelete.mutateAsync({ id: folder.id });
                            },
                          });
                        }}
                      />
                    )}
                  </>
                )
              }
            />
          ))}
          {canManage && (
            <Card
              icon="fa-plus"
              header={t("core:create_folder")}
              variant="action"
              onClick={() => openModal(TemplateFolderCreateModal, { module })}
            />
          )}
        </>
      )}
    </Stack>
  );
};
